import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import {motion, useScroll, useSpring, useTransform} from 'framer-motion'
import {useLinkResolver} from 'prismicio'
import type {ButtonDocument} from 'prismicio-types'
import {useEffect, useRef, useState} from 'react'
import {STRESSTIMONIALS_MOBILE_ANIMATION_STYLES} from 'src/common/constants/stresstimonialsAnimations'
import {Container} from 'src/common/Container'
import Drawer from 'src/common/Drawer'
import {getButtonsData} from 'src/common/helpers'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {PlusButton} from 'src/common/PlusButton'
import {StresstimonialsDrawerContent} from 'src/common/StresstimonialsDrawerContent'

type DefaultStresstimonialsCardProps = {
  slice: Extract<Content.StresstimonialsCardSlice, {variation: 'default'}>
  pageUid: string
  componentIndex: number
  buttonsData?: ButtonDocument[]
}

export const DefaultStresstimonialsCard = ({
  slice,
  pageUid,
  componentIndex,
  buttonsData,
}: DefaultStresstimonialsCardProps) => {
  const {linkResolver} = useLinkResolver()
  const domainCountry = useGetDomainCountry()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Track scroll progress through the component (desktop only)
  const {scrollYProgress} = useScroll({
    target: containerRef,
    offset: ['start 95%', 'start 60%'],
  })

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Intersection Observer for mobile CSS animation trigger
  useEffect(() => {
    if (!isMobile || !containerRef.current) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting)
        })
      },
      {
        threshold: 0.2,
        rootMargin: '0px',
      },
    )

    observer.observe(containerRef.current)

    return () => observer.disconnect()
  }, [isMobile])

  const {primaryButtonData} = getButtonsData({
    buttonsData,
    primaryButton: slice.primary.cta_button,
  })

  const isPositionLeft = slice.primary.position === 'Left'
  const hasExtraGap = slice.primary.extra_gap_from_side === true
  const hasAdditionalImage = isFilled.image(slice.primary.additional_image)

  // Animate the entire section from left or right based on card position
  const animationDistance = isMobile ? 50 : 100

  const rawSectionX = useTransform(
    scrollYProgress,
    [0, 1],
    [isPositionLeft ? -animationDistance : animationDistance, 0],
  )

  // Add spring animation for smooth experience
  const sectionX = useSpring(rawSectionX, {
    stiffness: 150,
    damping: 25,
    mass: 0.5,
  })

  const CardWrapper = motion.div

  return (
    <>
      <style>{STRESSTIMONIALS_MOBILE_ANIMATION_STYLES}</style>
      <Container
        backgroundColor="Transparent"
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="!py-[30px] md:!py-[90px]"
      >
        <div ref={containerRef} className="mx-auto w-full max-w-[1280px] px-4 md:px-5">
          <CardWrapper
            className={cn('flex items-start gap-12 md:gap-20', {
              'flex-1 justify-between': hasAdditionalImage,
              'md:flex-auto md:justify-start': hasAdditionalImage && isPositionLeft,
              'md:flex-auto md:justify-end': hasAdditionalImage && !isPositionLeft,
              'justify-start': !hasAdditionalImage && isPositionLeft,
              'justify-end': !hasAdditionalImage && !isPositionLeft,
              'mobile-card-animated': isMobile,
              'mobile-card-visible': isMobile && isVisible,
            })}
            style={isMobile ? {} : {x: sectionX}}
            {...(!isMobile && {style: {x: sectionX}})}
          >
            {isPositionLeft && (hasAdditionalImage || hasExtraGap) && (
              <div className="flex w-[146px] shrink-0 items-start justify-center md:w-[216px]">
                {hasAdditionalImage && (
                  <PrismicNextImage field={slice.primary.additional_image} className="h-auto max-w-full" />
                )}
              </div>
            )}

            <div
              className="group flex w-[147px] max-w-[216px] cursor-pointer flex-col gap-4 md:w-full"
              onClick={(e) => {
                e.stopPropagation()

                trackMossAnalytics({
                  event: 'interaction',
                  interaction: {
                    element: `StresstimonialsCard-${
                      isFilled.keyText(slice.primary.drawer_title) ? slice.primary.drawer_title : 'unknown'
                    }`,
                    elementText: 'card',
                    location: `stresstimonials-card-block`,
                    type: 'click',
                  },
                  page: {
                    domainCountry,
                    type: 'stresstimonials',
                  },
                })

                setIsDrawerOpen(true)
              }}
            >
              {isFilled.keyText(slice.primary.eyebrow) && (
                <div className="flex flex-col gap-2.5">
                  <div className="h-px w-full border-b border-dashed border-moss-grey-400" />
                  <p className="text-[11px] font-semibold uppercase tracking-[0.66px] text-moss-grey-400">
                    {slice.primary.eyebrow}
                  </p>
                  <div className="h-px w-full border-b border-dashed border-moss-grey-400" />
                </div>
              )}

              {isFilled.image(slice.primary.image) && (
                <div
                  className={cn(
                    'relative w-full overflow-hidden rounded-lg bg-moss-grey-100 ring-0 transition-all duration-300 group-hover:ring-2 group-hover:ring-green-neon',
                    {
                      'aspect-[238/150]': slice.primary.image_aspect_ratio === 'Horizontal',
                      'h-[200px] md:h-[288px]': slice.primary.image_aspect_ratio !== 'Horizontal',
                    },
                  )}
                >
                  <PrismicNextImage
                    field={slice.primary.image}
                    className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <PlusButton className="absolute bottom-2.5 right-2.5" />
                </div>
              )}

              {isFilled.keyText(slice.primary.bottom_comment) && (
                <div className="flex flex-col gap-3.5">
                  <div className="h-px w-full border-b border-dashed border-moss-grey-400" />
                  <p className="hyphens-auto text-18 font-semibold uppercase leading-normal tracking-[0.96px] text-moss-black">
                    {slice.primary.bottom_comment}
                  </p>
                  <div className="h-px w-full border-b border-dashed border-moss-grey-400" />
                </div>
              )}
            </div>

            {!isPositionLeft && (hasAdditionalImage || hasExtraGap) && (
              <div className="flex w-[146px] shrink-0 items-start justify-center md:w-[216px]">
                {hasAdditionalImage && (
                  <PrismicNextImage field={slice.primary.additional_image} className="h-auto max-w-full" />
                )}
              </div>
            )}
          </CardWrapper>
        </div>

        <Drawer isOpen={isDrawerOpen} setIsOpen={setIsDrawerOpen}>
          <StresstimonialsDrawerContent
            video_source={slice.primary.video_source}
            fallback_image={slice.primary.drawer_video_fallback_image}
            drawer_title={slice.primary.drawer_title}
            drawer_bio_title={slice.primary.drawer_bio_title}
            bio_info={slice.primary.bio_info}
            free_text={slice.primary.free_text}
            drawer_image={slice.primary.drawer_image}
            free_text_2={slice.primary.free_text_2}
            cta_eyebrow={slice.primary.cta_eyebrow}
            cta_title={slice.primary.cta_title}
            cta_description={slice.primary.cta_description}
            primaryButtonData={primaryButtonData}
            linkResolver={linkResolver}
            componentIndex={componentIndex}
            pageUid={pageUid}
            componentName="stresstimonials-card"
          />
        </Drawer>
      </Container>
    </>
  )
}
